import { useState, useCallback, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Input } from '@/components/ui/Input/Input';
import { Logo } from '@/components/ui/Logo/Logo';
import { Capacitor } from '@capacitor/core';
import { supabase, authRedirectUrl } from '@/lib/supabase';
import { startOAuth } from '@/lib/oauth';
import { useUser } from '@/constants/UserContext';
import { clearVolunteerOAuthSignupIntent } from '@/constants/volunteerOAuthSignup';
import { openExternalUrl } from '@/utils/openUrl';
import { getWebAppBaseUrl } from '@/utils/webAppUrl';
import { useResetOAuthInFlight } from '@/hooks/useResetOAuthInFlight';
import { OAuthTermsNotice } from '@/components/auth/OAuthTermsNotice';

interface LoginFormProps {
  onLoginSuccess: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 262">
    <path fill="#4285f4" d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622l38.755 30.023l2.685.268c24.659-22.774 38.875-56.282 38.875-96.027" />
    <path fill="#34a853" d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055c-34.523 0-63.824-22.773-74.269-54.25l-1.531.13l-40.298 31.187l-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1" />
    <path fill="#fbbc05" d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82c0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602z" />
    <path fill="#eb4335" d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0C79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251" />
  </svg>
);

const AppleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 814 1000">
    <path fill="currentColor" d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.9 0 663.1 0 541.8c0-207.7 135.4-317.5 269-317.5 70.8 0 129.9 46.4 174.3 46.4 42.7 0 109.6-49.5 188.9-49.5 30.5 0 110.7 2.6 167.4 79.2zm-164.8-186.1c31.5-37.8 54.3-90.8 54.3-143.9 0-7.4-.6-14.9-1.9-21.1-51.6 1.9-112.5 34.5-149.2 77.2-28.5 32.5-56.1 85.5-56.1 139.5 0 8.3 1.3 16.5 1.9 19.1 3.2.6 8.4 1.3 13.6 1.3 46.5 0 103.1-31.2 137.4-72.1z" />
  </svg>
);

const SocialButton = ({
  icon,
  label,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
  onClick?: () => void;
}) => (
  <chakra.button
    type="button"
    w="full"
    h="11"
    px="4"
    display="flex"
    alignItems="center"
    justifyContent="center"
    gap="3"
    borderRadius="xl"
    border="1px solid"
    borderColor="volyx.borderLight"
    bg="white"
    color="volyx.text"
    fontSize="sm"
    fontWeight="medium"
    fontFamily="body"
    cursor={disabled ? 'not-allowed' : 'pointer'}
    opacity={disabled ? 0.55 : 1}
    _hover={disabled ? {} : { bg: 'gray.50' }}
    _focus={{ outline: 'none' }}
    disabled={disabled}
    onClick={disabled ? undefined : onClick}
  >
    {icon}
    <span>{label}</span>
  </chakra.button>
);

export const LoginForm = ({
  onLoginSuccess,
  loading,
  setLoading,
}: LoginFormProps) => {
  const navigate = useNavigate();
  const { setUser } = useUser();

  // On native, send users to the web forgot-password form so the whole
  // password-reset flow happens on the web app (app.volyx.app).
  const handleForgotPassword = () => {
    if (Capacitor.isNativePlatform()) {
      void openExternalUrl(`${getWebAppBaseUrl()}/#/forgot-password`);
    } else {
      navigate('/forgot-password');
    }
  };

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthProvider, setOauthProvider] = useState<'google' | 'apple' | null>(null);

  useResetOAuthInFlight(useCallback(() => setOauthProvider(null), []));

  const handleGoogleLogin = async () => {
    setError(null);
    setOauthProvider('google');
    try {
      clearVolunteerOAuthSignupIntent();
      await startOAuth('google', authRedirectUrl);
      // Web: Supabase redirects. Native: the in-app browser opens and the
      // appUrlOpen deep-link listener finishes sign-in.
    } catch (err) {
      setError((err instanceof Error && err.message) || 'Failed to start Google login');
      setOauthProvider(null);
    }
  };

  const handleAppleLogin = async () => {
    setError(null);
    setOauthProvider('apple');
    try {
      clearVolunteerOAuthSignupIntent();
      await startOAuth('apple', authRedirectUrl);
    } catch (err) {
      setError((err instanceof Error && err.message) || 'Failed to start Apple login');
      setOauthProvider(null);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });

      if (signInError) throw signInError;

      if (data.user) {
        clearVolunteerOAuthSignupIntent();

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.user.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') throw profileError;

        if (!profile) {
          const { error: insertError } = await supabase.from('profiles').insert([
            {
              id: data.user.id,
              email: formData.email.trim().toLowerCase(),
              role: 'volunteer',
              hours: 0,
              points: 0,
              created_at: new Date().toISOString(),
            },
          ]);
          if (insertError) throw insertError;
          const { data: newProfile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', data.user.id)
            .single();
          setUser(newProfile);
        } else {
          setUser(profile);
        }

        onLoginSuccess();
      }
    } catch (err) {
      const raw = (err instanceof Error && err.message) || '';
      // Supabase returns the same "Invalid login credentials" for a wrong password and for an
      // account that only has a social (Google/Apple) identity and no password. Nudge users
      // toward the social buttons so a passwordless OAuth account isn't a dead end.
      const invalidCreds = /invalid login credentials/i.test(raw);
      setError(
        invalidCreds
          ? 'Incorrect email or password. If you signed up with Google or Apple, use the buttons above to continue.'
          : raw || 'An error occurred during login',
      );
      setLoading(false);
    }
  };

  return (
    <>
      {/* Card body — grows to fill the screen on phones, centering its content */}
      <Box p="8" flex="1" display="flex" flexDirection="column" justifyContent="center">
        {/* Logo + heading */}
        <Box mb="6">
          <Flex justify="center">
            <Logo mb="2" />
          </Flex>
          <Text
            as="h1"
            mt="0"
            fontSize="2xl"
            fontWeight="semibold"
            lineHeight="snug"
            color="volyx.text"
            textAlign="center"
          >
            Sign in to continue
          </Text>
        </Box>

        {/* Social buttons */}
        <Flex direction="column" gap="3" mb="6">
          <SocialButton
            icon={<GoogleIcon />}
            label={oauthProvider === 'google' ? 'Connecting to Google…' : 'Continue with Google'}
            disabled={oauthProvider !== null || loading}
            onClick={handleGoogleLogin}
          />
          <SocialButton
            icon={<AppleIcon />}
            label={oauthProvider === 'apple' ? 'Connecting to Apple…' : 'Continue with Apple'}
            disabled={oauthProvider !== null || loading}
            onClick={handleAppleLogin}
          />
          <OAuthTermsNotice />
        </Flex>

        {/* Divider */}
        <Flex align="center" mb="6">
          <Box flex="1" h="1px" bg="volyx.borderLight" />
          <Text px="3" fontSize="sm" color="volyx.textSecondary">or</Text>
          <Box flex="1" h="1px" bg="volyx.borderLight" />
        </Flex>

        {/* Form */}
        <Flex as="form" onSubmit={handleSubmit} direction="column" gap="4">
          <Input
            id="login-email"
            name="email"
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            autoComplete="email"
            required
            error={error && formData.email ? error : undefined}
          />

          <Input
            id="login-password"
            name="password"
            type="password"
            placeholder="Password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            autoComplete="current-password"
            required
            showPasswordToggle
            showPassword={showPassword}
            onPasswordToggle={() => setShowPassword(!showPassword)}
            error={error && formData.password ? error : undefined}
          />

          {error && !formData.email && !formData.password && (
            <Box
              p="3"
              bg="rgba(255,59,48,0.1)"
              color="volyx.error"
              borderRadius="lg"
              fontSize="sm"
              textAlign="center"
            >
              {error}
            </Box>
          )}

          <chakra.button
            alignSelf="flex-end"
            bg="transparent"
            border="none"
            color="volyx.primary"
            fontSize="sm"
            fontWeight="medium"
            cursor="pointer"
            p="0"
            mt="-1"
            transition="opacity 0.2s"
            _hover={{ opacity: 0.7 }}
            _focus={{ outline: 'none', textDecoration: 'underline' }}
            type="button"
            onClick={handleForgotPassword}
          >
            Forgot your password?
          </chakra.button>

          <chakra.button
            type="submit"
            w="full"
            px="6"
            py="3"
            borderRadius="xl"
            fontWeight="semibold"
            fontFamily="body"
            transition="all 0.3s"
            cursor="pointer"
            border="none"
            outline="none"
            bg="volyx.primary"
            color="white"
            boxShadow="md"
            _hover={{ boxShadow: 'lg', transform: 'translateY(-2px) scale(1.02)' }}
            _disabled={{ opacity: 0.7, cursor: 'not-allowed' }}
            disabled={loading}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </chakra.button>
        </Flex>
      </Box>

      {/* Card footer */}
      <Box
        borderTop="1px solid"
        borderColor="volyx.borderLight"
        px="8"
        pt="5"
        pb={{ base: 'calc(env(safe-area-inset-bottom, 0px) + 1.25rem)', md: '5' }}
        textAlign="center"
      >
        <Text as="span" color="volyx.textSecondary" fontSize="sm">
          Don't have an account?{' '}
        </Text>
        <chakra.button
          bg="transparent"
          border="none"
          color="volyx.primary"
          fontSize="sm"
          fontWeight="semibold"
          cursor="pointer"
          p="0"
          transition="opacity 0.2s"
          _hover={{ opacity: 0.7 }}
          _focus={{ outline: 'none', textDecoration: 'underline' }}
          type="button"
          onClick={() => navigate('/signup')}
        >
          Sign up
        </chakra.button>
      </Box>
    </>
  );
};
